import * as React from 'react';
import { useEffect } from 'react';

import { api } from '@api/reduxApi';
import { contactAccessOrAffiliation } from '@components/CandidatesApp/CandidatesShow/components/ContactAccess';
import { Button, Modal, ModalHeading, Text } from '@components/common';
import { track } from '@components/tracking';
import { useFeature } from '@hooks/useFeature';
import { useToaster } from '@stores/toaster';

import { useDeleteTeamMutation } from '../api';
import * as toasts from '../toasts';

type Props = {
  team: Team;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onNavigateToStudies?: (teamId: number) => void;
};

export const DeleteTeamModal: React.FC<React.PropsWithChildren<Props>> = ({
  team,
  open,
  onClose,
  onSuccess,
  onNavigateToStudies
}) => {
  const showToast = useToaster();
  const hasTeamAccessControl = useFeature('team_access_control');
  const { data: studiesData, isLoading: isLoadingStudies } = api.useGetStudiesPagedQuery(
    { teamId: team.id },
    { skip: !hasTeamAccessControl || !open }
  );

  const hasStudies = studiesData?.data && studiesData.data.length > 0;

  const [deleteTeam, { isLoading, isSuccess, isError }] = useDeleteTeamMutation();

  useEffect(() => {
    if (isSuccess) {
      showToast(toasts.successDeleteTeam());
      track('deleted_team');
      onSuccess();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      showToast(toasts.failedDeleteTeam());
    }
  }, [isError]);

  useEffect(() => {
    if (open) {
      track('viewed_delete_team_modal');
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleSubmit = () => {
    deleteTeam({ id: team.id });
  };

  const handleNavigateToStudies = () => {
    onClose();
    onNavigateToStudies?.(team.id);
  };

  const renderContent = () => {
    // Cannot delete: Team has studies and team access control is enabled
    if (hasTeamAccessControl && hasStudies) {
      return (
        <>
          <ModalHeading className='mb-6'>Unable to delete {team.name}</ModalHeading>

          <Text h='400' mb='4'>
            There are currently {studiesData?.data.length} studies allocated to this team. In order to delete this team,
            please change the team on these studies, or delete the studies.
          </Text>
        </>
      );
    }

    // Standard deletion modal for when team can be deleted
    // (either no team access control or team has no studies)
    return (
      <>
        <ModalHeading className='mb-6'>Delete {team.name}?</ModalHeading>

        <Text h='400' mb='2'>
          By deleting {team.name}, you understand that:
        </Text>
        <ul className='list-disc pl-4'>
          {!hasTeamAccessControl && (
            <li>
              <Text h='400'>Any studies in the team will be removed from the team, but remain in the workspace.</Text>
            </li>
          )}
          <li>
            <Text h='400'>
              All candidates whose {contactAccessOrAffiliation(hasTeamAccessControl).toLowerCase()} is restricted to the
              team, will no longer be restricted.
            </Text>
          </li>
        </ul>
      </>
    );
  };

  const renderFooter = () => {
    // Cannot delete: Show navigation to studies instead of delete button
    if (hasTeamAccessControl && hasStudies) {
      return (
        <div className='flex justify-end space-x-4'>
          <Button onClick={onClose}>Cancel</Button>
          <Button primary onClick={handleNavigateToStudies}>
            Go to Team studies
          </Button>
        </div>
      );
    }

    // Standard delete confirmation buttons
    return (
      <div className='flex justify-end space-x-4'>
        <Button disabled={isLoading} onClick={onClose}>
          Cancel & don't delete
        </Button>
        <Button loading={isLoading} disabled={isLoading} danger onClick={handleSubmit}>
          Delete team
        </Button>
      </div>
    );
  };

  return (
    <Modal onClose={onClose} size='md' renderFooter={() => renderFooter()}>
      {isLoadingStudies ? <Text className='py-4 text-center'>Loading...</Text> : renderContent()}
    </Modal>
  );
};
